import { Component, PropsWithChildren, ReactNode } from 'react';
import { ErrorReference, reportWithReference } from '../../utils/error-reference';
import { ErrorFallbackMessage } from './ErrorFallbackMessage';

interface ErrorBoundaryProps extends PropsWithChildren {
  // Rendered in place of the crashed subtree. Defaults to a static message; pass `null` when a
  // separately-mounted, always-available renderer (e.g. a global error modal) already covers
  // showing something to the user, so the crashed subtree doesn't also leave its own placeholder.
  fallback?: ReactNode;
  // Side effect to run in addition to the unconditional Datadog report below (e.g. surfacing the
  // real error modal via a redux dispatch) — kept optional/pluggable so a boundary that already
  // renders its own `fallback` doesn't also need a caller-supplied effect.
  onError?: (error: unknown) => void;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  { hasError: boolean; reference?: ErrorReference }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    const { onError } = this.props;
    console.error(error);
    // The same FE- fingerprint goes to RUM and into the default fallback below.
    this.setState({ reference: reportWithReference(error, 'ErrorBoundary') });
    onError?.(error);
  }

  render() {
    const { hasError, reference } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // `fallback === undefined` (prop omitted) means "use the default message" -- an explicit
      // `fallback={null}` (prop passed, value null) must still render nothing, so this can't be a
      // `??`/`||` fallback chain.
      return fallback !== undefined ? (
        fallback
      ) : (
        <ErrorFallbackMessage className='error-boundary' reference={reference} />
      );
    }
    return children;
  }
}
